import { useState } from "react";
import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  Loader2,
  PencilLine,
  Upload,
  FolderOpen,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, formatDate, formatDocumentTypeLabel } from "@/lib/utils";

export type ProjectDocumentListItem = Doc<"projectDocuments"> & {
  folderPath: string;
  latestRevision: Doc<"projectDocumentRevisions"> | null;
  revisions: Doc<"projectDocumentRevisions">[];
};

interface ProjectDocumentsTableProps {
  documents: ProjectDocumentListItem[];
  selectedDocumentIds?: Set<string>;
  areAllDocumentsSelected?: boolean;
  hasSomeDocumentsSelected?: boolean;
  onToggleAllDocuments?: (checked: boolean) => void;
  onToggleDocumentSelection?: (documentId: Id<"projectDocuments">, checked: boolean) => void;
  onOpenDocument: (documentId: Id<"projectDocuments">) => void;
  onRenameDocument?: (document: ProjectDocumentListItem) => void;
  onUploadRevision?: (documentId: Id<"projectDocuments">) => void;
  onMoveDocument?: (document: ProjectDocumentListItem) => void;
  isUploadingRevision?: boolean;
  revisionUploadDocumentId?: Id<"projectDocuments"> | null;
  showFolderColumn?: boolean;
  emptyMessage?: string;
  compact?: boolean;
}

function getDocumentFolderLabel(folderPath: string): string {
  return folderPath || "Project Documents";
}

export function ProjectDocumentsTable({
  documents,
  selectedDocumentIds,
  areAllDocumentsSelected = false,
  hasSomeDocumentsSelected = false,
  onToggleAllDocuments,
  onToggleDocumentSelection,
  onOpenDocument,
  onRenameDocument,
  onUploadRevision,
  onMoveDocument,
  isUploadingRevision = false,
  revisionUploadDocumentId = null,
  showFolderColumn = false,
  emptyMessage = "No documents in this location yet.",
  compact = false,
}: ProjectDocumentsTableProps) {
  const [openRevisionDocumentIds, setOpenRevisionDocumentIds] = useState<Set<string>>(() => new Set());
  const showSelectionColumn = Boolean(selectedDocumentIds && onToggleDocumentSelection);
  const showActionsColumn = Boolean(onRenameDocument || onUploadRevision || onMoveDocument);
  const emptyColumnCount =
    4 + (showSelectionColumn ? 1 : 0) + (showFolderColumn ? 1 : 0) + (showActionsColumn ? 1 : 0);

  const handleToggleRevisionHistory = (documentId: Id<"projectDocuments">) => {
    setOpenRevisionDocumentIds((current) => {
      const next = new Set(current);
      const key = String(documentId);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <Table className={compact ? "table-fixed text-xs" : undefined}>
      <TableHeader>
        <TableRow>
          {showSelectionColumn ? (
            <TableHead className={compact ? "h-9 w-[60px] px-2" : "w-[92px]"}>
              <Checkbox
                aria-label="Select all visible documents"
                checked={areAllDocumentsSelected ? true : hasSomeDocumentsSelected ? "indeterminate" : false}
                onCheckedChange={(checked) => {
                  onToggleAllDocuments?.(checked === true);
                }}
              />
            </TableHead>
          ) : null}
          <TableHead className={compact ? "h-9 w-[31%] px-2" : undefined}>Name</TableHead>
          {showFolderColumn ? <TableHead className={compact ? "h-9 w-[18%] px-2" : undefined}>Folder</TableHead> : null}
          <TableHead className={compact ? "h-9 w-[24%] px-2" : undefined}>Latest Revision</TableHead>
          <TableHead className={compact ? "h-9 w-[12%] px-2" : undefined}>Type</TableHead>
          <TableHead className={compact ? "h-9 w-[15%] px-2" : "w-[140px]"}>Updated</TableHead>
          {showActionsColumn ? <TableHead className={compact ? "h-9 w-[44px] px-2" : "w-[60px]"} /> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.length === 0 ? (
          <TableRow>
            <TableCell colSpan={emptyColumnCount} className="py-10 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          documents.flatMap((document) => {
            const sortedRevisions = (document.revisions ?? [])
              .slice()
              .sort(
                (a, b) =>
                  b.revisionNumber - a.revisionNumber || String(b._id).localeCompare(String(a._id))
              );
            const latestRevision = sortedRevisions[0] ?? document.latestRevision ?? null;
            const historyRevisions = sortedRevisions.slice(1);
            const isRevisionHistoryOpen = openRevisionDocumentIds.has(String(document._id));
            const documentRow = (
              <TableRow key={document._id}>
                {showSelectionColumn ? (
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Checkbox
                        aria-label={`Select ${document.name}`}
                        checked={selectedDocumentIds?.has(String(document._id)) ?? false}
                        onCheckedChange={(checked) => {
                          onToggleDocumentSelection?.(document._id, checked === true);
                        }}
                      />
                      {historyRevisions.length > 0 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          aria-expanded={isRevisionHistoryOpen}
                          aria-label={`${isRevisionHistoryOpen ? "Hide" : "Show"} revisions for ${document.name}`}
                          onClick={() => handleToggleRevisionHistory(document._id)}
                        >
                          {isRevisionHistoryOpen ? (
                            <ChevronDown className="size-4" />
                          ) : (
                            <ChevronRight className="size-4" />
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                ) : null}
                <TableCell className={compact ? "px-2 py-2" : "min-w-[280px]"}>
                  <button
                    type="button"
                    onClick={() => onOpenDocument(document._id)}
                    className="w-full min-w-0 text-left"
                    aria-label={`Open ${document.name}`}
                  >
                    <div className={compact ? "min-w-0" : "min-w-0 space-y-1"}>
                      <p className="truncate font-medium">{document.name}</p>
                      {!compact ? (
                        <p className="text-xs text-muted-foreground">
                          {document.revisionCount} revision{document.revisionCount === 1 ? "" : "s"}
                        </p>
                      ) : null}
                    </div>
                  </button>
                </TableCell>
                {showFolderColumn ? (
                  <TableCell className={cn("truncate text-muted-foreground", compact ? "px-2 py-2 text-xs" : "min-w-[180px] text-sm")}>
                    {getDocumentFolderLabel(document.folderPath)}
                  </TableCell>
                ) : null}
                <TableCell className={compact ? "px-2 py-2" : undefined}>
                  {latestRevision ? (
                    <div className={compact ? "flex min-w-0 items-center gap-1.5" : "flex min-w-0 flex-col items-start gap-1"}>
                      <span className="flex max-w-full items-center gap-2">
                        <Badge variant="outline" className={compact ? "shrink-0 px-1.5 py-0 text-[10px]" : "shrink-0"}>
                          R{latestRevision.revisionNumber}
                        </Badge>
                        <span className="truncate text-sm font-medium">{latestRevision.revisionName}</span>
                      </span>
                      {!compact ? (
                        <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                          {latestRevision.originalFilename}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">No revisions</span>
                  )}
                </TableCell>
                <TableCell className={compact ? "truncate px-2 py-2 text-xs" : "text-sm"}>
                  {formatDocumentTypeLabel(latestRevision?.effectiveDocumentType)}
                </TableCell>
                <TableCell className={compact ? "px-2 py-2 text-xs text-muted-foreground" : "text-sm text-muted-foreground"}>
                  {formatDate(document.updatedAt)}
                </TableCell>
                {showActionsColumn ? (
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-xs" aria-label={`Actions for ${document.name}`}>
                          <EllipsisVertical className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {onRenameDocument ? (
                          <DropdownMenuItem
                            onSelect={(event) => {
                              event.preventDefault();
                              onRenameDocument(document);
                            }}
                          >
                            <PencilLine className="size-4" />
                            Rename
                          </DropdownMenuItem>
                        ) : null}
                        {onUploadRevision ? (
                          <DropdownMenuItem
                            disabled={isUploadingRevision && revisionUploadDocumentId === document._id}
                            onSelect={(event) => {
                              event.preventDefault();
                              onUploadRevision(document._id);
                            }}
                          >
                            {isUploadingRevision && revisionUploadDocumentId === document._id ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Upload className="size-4" />
                            )}
                            Upload revision
                          </DropdownMenuItem>
                        ) : null}
                        {onMoveDocument ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={(event) => {
                                event.preventDefault();
                                onMoveDocument(document);
                              }}
                            >
                              <FolderOpen className="size-4" />
                              Move
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                ) : null}
              </TableRow>
            );

            if (!isRevisionHistoryOpen) {
              return [documentRow];
            }

            return [
              documentRow,
              ...historyRevisions.map((revision) => (
                <TableRow key={revision._id} className="bg-muted/15">
                  {showSelectionColumn ? <TableCell /> : null}
                  <TableCell className="min-w-[280px] pl-10 text-sm text-muted-foreground">
                    Previous revision
                  </TableCell>
                  {showFolderColumn ? <TableCell /> : null}
                  <TableCell>
                    <div className="flex min-w-0 flex-col items-start gap-1">
                      <span className="flex max-w-full items-center gap-2">
                        <Badge variant="secondary" className="shrink-0">
                          R{revision.revisionNumber}
                        </Badge>
                        <span className="truncate text-sm font-medium">{revision.revisionName}</span>
                      </span>
                      <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                        {revision.originalFilename}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDocumentTypeLabel(revision.effectiveDocumentType)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(revision._creationTime)}
                  </TableCell>
                  {showActionsColumn ? <TableCell /> : null}
                </TableRow>
              )),
            ];
          })
        )}
      </TableBody>
    </Table>
  );
}
